/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  IssuerFieldMismatch,
  IssuerFieldMismatch$inboundSchema,
} from "./issuerfieldmismatch.js";

/**
 * Authoritative impact summary for consolidating a tenant remote_session_issuer onto a global one: how many clients move, which MCP servers are affected, every blocker that would make the migration fail, and how many tenant-owned clients the target already carries.
 */
export type IssuerMigratePreflight = {
  /**
   * TRUE when the migration would succeed: no active identity-chaining bindings, endpoint mismatches, conflicting MCP-server bindings, or user-session issuers that trust the source.
   */
  canMigrate: boolean;
  /**
   * Number of non-deleted remote_session_clients that would be re-pointed from the source issuer to the target issuer.
   */
  clientCount: number;
  /**
   * Display names of MCP servers where both the source and the target issuer already have a client bound. Non-empty blocks the migration; detach one client per listed server and retry.
   */
  conflictingMcpServerNames: Array<string>;
  /**
   * Number of active identity-chaining bindings on the source. Non-zero blocks migration; explicitly unlink these bindings before migration, then prepare new bindings for the target.
   */
  emaBindingCount: number;
  /**
   * The authorization-server metadata fields (issuer, token_endpoint, authorization_endpoint) that differ between source and target, with both sides' values. Non-empty blocks the migration.
   */
  endpointMismatches: Array<IssuerFieldMismatch>;
  /**
   * Display names of MCP servers attached to the source issuer's clients.
   */
  mcpServerNames: Array<string>;
  /**
   * Number of tenant-owned remote_session_clients already registered with the target issuer, BEFORE this migration. Any non-zero value blocks deleting the target issuer, and only the owning organizations can clear it, so a successful migration is effectively one-way.
   */
  targetTenantClientCount: number;
  /**
   * Number of user_session_issuers that trust the source. Any non-zero value blocks migration.
   */
  trustedUserSessionIssuerCount: number;
  /**
   * Non-blocking divergences (oidc, passthrough, scopes_supported), with both sides' values. The target issuer's values become authoritative for the migrated clients.
   */
  warnings: Array<IssuerFieldMismatch>;
};

/** @internal */
export const IssuerMigratePreflight$inboundSchema: z.ZodMiniType<
  IssuerMigratePreflight,
  unknown
> = z.pipe(
  z.object({
    can_migrate: z.boolean(),
    client_count: z.int(),
    conflicting_mcp_server_names: z.array(z.string()),
    ema_binding_count: z.int(),
    endpoint_mismatches: z.array(IssuerFieldMismatch$inboundSchema),
    mcp_server_names: z.array(z.string()),
    target_tenant_client_count: z.int(),
    trusted_user_session_issuer_count: z.int(),
    warnings: z.array(IssuerFieldMismatch$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "can_migrate": "canMigrate",
      "client_count": "clientCount",
      "conflicting_mcp_server_names": "conflictingMcpServerNames",
      "ema_binding_count": "emaBindingCount",
      "endpoint_mismatches": "endpointMismatches",
      "mcp_server_names": "mcpServerNames",
      "target_tenant_client_count": "targetTenantClientCount",
      "trusted_user_session_issuer_count": "trustedUserSessionIssuerCount",
    });
  }),
);

export function issuerMigratePreflightFromJSON(
  jsonString: string,
): SafeParseResult<IssuerMigratePreflight, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => IssuerMigratePreflight$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'IssuerMigratePreflight' from JSON`,
  );
}
